import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  ChevronLeft,
  ChevronRight,
  Plus,
  Search,
  CheckCircle2,
  Pencil,
  History,
  Upload,
  Download,
  X,
  Repeat,
  Bell,
  CalendarOff,
  Lock,
  LockOpen,
  ChevronDown,
  Trash2,
} from 'lucide-react';
import Layout from '../components/Layout';
import CompanyTaskModal from '../components/CompanyTaskModal';
import { AuthContext } from '../contexts/AuthContext';
import api from '../utils/api';
import { canAccessBossModules } from '../utils/roleUtils';
import {
  formatTaskAmount,
  formatTaskDate,
  formatTaskMonth,
  getCompanyTaskStatusMeta,
  getCurrentMonthInputValue,
  mergeCategoryOptions,
} from '../utils/companyTaskUtils';

const pad2 = (value) => String(value).padStart(2, '0');

const shiftMonthKey = (monthKey, delta) => {
  const [year, month] = monthKey.split('-').map(Number);
  const date = new Date(year, month - 1 + delta, 1);
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}`;
};

const daysInMonth = (monthKey) => {
  const [year, month] = monthKey.split('-').map(Number);
  return new Date(year, month, 0).getDate();
};

const buildDueDate = (monthKey, dueDay) => {
  const day = Math.min(daysInMonth(monthKey), Math.max(1, Number(dueDay) || 1));
  return `${monthKey}-${pad2(day)}`;
};

const dueTimestamp = (dateString) => {
  if (!dateString) return Number.MAX_SAFE_INTEGER;
  const [year, month, day] = dateString.split('-').map(Number);
  return new Date(year, month - 1, day).getTime();
};

const PERIOD_LABELS = { monthly: 'Aylık', quarterly: '3 Aylık', yearly: 'Yıllık' };

const recurrenceToBackend = (recurrence, monthKey) => {
  const year = monthKey.slice(0, 4);
  switch (recurrence) {
    case 'quarterly':
      return { period: 'quarterly', endMonth: null };
    case 'yearly':
      return { period: 'yearly', endMonth: null };
    case 'year_end':
      return { period: 'monthly', endMonth: `${year}-12` };
    case 'monthly':
    default:
      return { period: 'monthly', endMonth: null };
  }
};

const getAssignedUserIds = (item) => item.assignedUserIds || (item.assignedUserId ? [item.assignedUserId] : []);

const isAssignedToUser = (item, userId) => getAssignedUserIds(item).some((assignedUserId) => String(assignedUserId) === String(userId));

const formatAssignedUsers = (item) => item.assignedUsernames || (item.assignedUsers || []).map((assignedUser) => assignedUser.username).join(', ') || item.assignedUsername || '';

const STATUS_FILTER_OPTIONS = [
  { value: 'open', label: 'Açık işler' },
  { value: 'all', label: 'Tüm durumlar' },
  { value: 'pending', label: 'Bekleyenler' },
  { value: 'overdue', label: 'Gecikenler' },
  { value: 'completed', label: 'Tamamlananlar' },
  { value: 'missingProof', label: 'Belge eksik' },
  { value: 'skipped', label: 'Pas geçilenler' },
];

const PRIORITY_ORDER = ['Geciken', 'Bugün', 'Belgesi eksik', 'Yaklaşan 7 gün', 'Daha sonra', 'Tamamlanan'];

const formatAuditDateTime = (value) => {
  if (!value) return '-';
  let parsed = new Date(value);
  if (Number.isNaN(parsed.getTime()) && typeof value === 'string') {
    const [datePart, timePart = '00:00:00'] = value.split(' ');
    const [year, month, day] = datePart.split('-').map(Number);
    const [hour, minute] = timePart.split(':').map(Number);
    parsed = new Date(year, (month || 1) - 1, day || 1, hour || 0, minute || 0);
  }
  if (Number.isNaN(parsed.getTime())) return '-';
  return new Intl.DateTimeFormat('tr-TR', {
    day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  }).format(parsed);
};

const CompanyTasks = () => {
  const { user } = useContext(AuthContext);
  const canEdit = user?.role === 'admin';
  const canView = canAccessBossModules(user?.role) || user?.role === 'staff';

  const [selectedMonth, setSelectedMonth] = useState(getCurrentMonthInputValue());
  const [searchTerm, setSearchTerm] = useState('');
  const [ownerFilter, setOwnerFilter] = useState(canEdit ? 'all' : 'mine');
  const [statusFilter, setStatusFilter] = useState('open');
  const [groupMode, setGroupMode] = useState('priority');
  const [collapsed, setCollapsed] = useState(() => new Set());

  const [monthData, setMonthData] = useState({
    items: [], templates: [], summary: null, categories: [], monthStatus: null,
  });
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
  const [taskToEdit, setTaskToEdit] = useState(null);
  const [taskToDelete, setTaskToDelete] = useState(null);
  const [isDeletingTask, setIsDeletingTask] = useState(false);
  const [isSavingTask, setIsSavingTask] = useState(false);
  const [isTogglingMonth, setIsTogglingMonth] = useState(false);

  const [isLogsOpen, setIsLogsOpen] = useState(false);
  const [logsTask, setLogsTask] = useState(null);
  const [taskLogs, setTaskLogs] = useState([]);
  const [isLoadingLogs, setIsLoadingLogs] = useState(false);

  const fetchUsers = async () => {
    try {
      const response = await api.get('/company-tasks/users');
      setUsers(response.data);
    } catch (error) {
      console.error('Kullanici listesi yuklenemedi', error);
    }
  };

  const fetchMonthData = useCallback(async (monthKey) => {
    setIsLoading(true);
    try {
      const response = await api.get('/company-tasks/monthly', { params: { month: monthKey } });
      setMonthData(response.data);
    } catch (error) {
      console.error('Idari takip verileri alinamadi', error);
      toast.error('İdari takip verileri yüklenemedi.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (canView) {
      fetchMonthData(selectedMonth);
      fetchUsers();
    }
  }, [selectedMonth, canView, fetchMonthData]);

  const isMonthClosed = Boolean(monthData.monthStatus?.isClosed);
  const categoryOptions = useMemo(() => mergeCategoryOptions(monthData.categories || []), [monthData.categories]);
  const templateMap = useMemo(() => {
    const map = {};
    (monthData.templates || []).forEach((t) => { map[t.id] = t; });
    return map;
  }, [monthData.templates]);

  const filteredItems = useMemo(() => monthData.items.filter((item) => {
    const search = searchTerm.trim().toLocaleLowerCase('tr-TR');
    const statusKey = item.displayStatus || item.status;
    const matchesSearch = !search || [item.title, item.category, item.responsible, item.notes, item.description, formatAssignedUsers(item)]
      .filter(Boolean).some((v) => v.toLocaleLowerCase('tr-TR').includes(search));
    const matchesOwner = ownerFilter === 'all' || isAssignedToUser(item, user?.id);
    let matchesStatus = true;
    if (statusFilter === 'open') matchesStatus = !item.isCompleted && !item.isSkipped;
    else if (statusFilter === 'missingProof') matchesStatus = Boolean(item.missingProof);
    else if (statusFilter !== 'all') matchesStatus = statusKey === statusFilter;
    return matchesSearch && matchesOwner && matchesStatus;
  }), [monthData.items, searchTerm, ownerFilter, statusFilter, user?.id]);

  const groupedItems = useMemo(() => {
    const groups = new Map();
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const upcomingLimit = today.getTime() + 7 * 24 * 60 * 60 * 1000;
    filteredItems.forEach((item) => {
      let key = item.category || 'Diğer';
      if (groupMode === 'priority') {
        const due = dueTimestamp(item.dueDate);
        if (item.isCompleted || item.isSkipped) key = 'Tamamlanan';
        else if (item.isOverdue) key = 'Geciken';
        else if (due === today.getTime()) key = 'Bugün';
        else if (item.missingProof) key = 'Belgesi eksik';
        else if (due <= upcomingLimit) key = 'Yaklaşan 7 gün';
        else key = 'Daha sonra';
      }
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(item);
    });
    groups.forEach((items) => items.sort((a, b) => dueTimestamp(a.dueDate) - dueTimestamp(b.dueDate)));
    return Array.from(groups.entries()).sort((a, b) => groupMode === 'priority'
      ? PRIORITY_ORDER.indexOf(a[0]) - PRIORITY_ORDER.indexOf(b[0])
      : a[0].localeCompare(b[0], 'tr'));
  }, [filteredItems, groupMode]);

  const monthSummary = useMemo(() => {
    const items = monthData.items;
    const total = items.length;
    const completed = items.filter((i) => i.isCompleted).length;
    const overdue = items.filter((i) => i.isOverdue).length;
    const missingProof = items.filter((i) => i.missingProof).length;
    const open = items.filter((i) => !i.isCompleted && !i.isSkipped).length;
    const rate = total > 0 ? Math.round((completed / total) * 100) : 0;
    return { total, completed, overdue, missingProof, open, rate };
  }, [monthData.items]);

  const upcomingCount = useMemo(() => {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const limit = today.getTime() + 7 * 24 * 60 * 60 * 1000;
    return monthData.items.filter((i) => !i.isCompleted && !i.isSkipped && !i.isOverdue
      && dueTimestamp(i.dueDate) >= today.getTime() && dueTimestamp(i.dueDate) <= limit).length;
  }, [monthData.items]);

  const hasWritePermission = useCallback((item) => {
    if (!canView || isMonthClosed) return false;
    if (canEdit) return true;
    return isAssignedToUser(item, user?.id);
  }, [canView, isMonthClosed, canEdit, user?.id]);

  const handleSubmitTask = async (result) => {
    const { mode, fields, recurrence, templateId, itemId } = result;
    setIsSavingTask(true);
    try {
      if (mode === 'create-oneoff') {
        await api.post('/company-tasks/items', {
          monthKey: selectedMonth,
          title: fields.title,
          category: fields.category,
          description: fields.description,
          dueDate: buildDueDate(selectedMonth, fields.dueDay),
          plannedAmount: fields.plannedAmount,
          currency: fields.currency,
          assignedUserIds: fields.assignedUserIds,
          requiresProof: fields.requiresProof,
        });
        toast.success('Görev eklendi.');
      } else if (mode === 'create-recurring') {
        const { period, endMonth } = recurrenceToBackend(recurrence, selectedMonth);
        await api.post('/company-tasks/templates', {
          title: fields.title,
          category: fields.category,
          description: fields.description,
          dueDay: fields.dueDay,
          defaultAmount: fields.plannedAmount,
          currency: fields.currency,
          assignedUserIds: fields.assignedUserIds,
          requiresProof: fields.requiresProof,
          period,
          startMonth: selectedMonth,
          endMonth,
        });
        toast.success('Tekrarlayan görev eklendi.');
      } else if (mode === 'update-series') {
        // 'Tek seferlik' secilirse seriyi bu aydan durdur; aksi halde yeni periyodu uygula.
        const { period, endMonth } = recurrence === 'once'
          ? { period: 'monthly', endMonth: selectedMonth }
          : recurrenceToBackend(recurrence, selectedMonth);
        await api.put(`/company-tasks/templates/${templateId}`, {
          title: fields.title,
          category: fields.category,
          description: fields.description,
          dueDay: fields.dueDay,
          defaultAmount: fields.plannedAmount,
          currency: fields.currency,
          assignedUserIds: fields.assignedUserIds,
          requiresProof: fields.requiresProof,
          period,
          endMonth,
          applyFromMonth: selectedMonth,
        });
        toast.success('Bu ay ve sonrası güncellendi.');
      } else if (mode === 'convert-recurring') {
        const { period, endMonth } = recurrenceToBackend(recurrence, selectedMonth);
        await api.post(`/company-tasks/items/${itemId}/convert-recurring`, {
          title: fields.title,
          category: fields.category,
          description: fields.description,
          dueDay: fields.dueDay,
          defaultAmount: fields.plannedAmount,
          currency: fields.currency,
          assignedUserIds: fields.assignedUserIds,
          requiresProof: fields.requiresProof,
          period,
          endMonth,
        });
        toast.success('Görev tekrarlıya çevrildi.');
      } else {
        await api.put(`/company-tasks/items/${itemId}`, {
          title: fields.title,
          category: fields.category,
          description: fields.description,
          dueDate: buildDueDate(selectedMonth, fields.dueDay),
          plannedAmount: fields.plannedAmount,
          currency: fields.currency,
          assignedUserIds: fields.assignedUserIds,
          requiresProof: fields.requiresProof,
        });
        toast.success('Görev güncellendi.');
      }
      setIsTaskModalOpen(false);
      setTaskToEdit(null);
      fetchMonthData(selectedMonth);
    } catch (error) {
      console.error('Görev kaydedilemedi', error);
      toast.error(error.response?.data?.message || 'Görev kaydedilemedi.');
    } finally {
      setIsSavingTask(false);
    }
  };

  const handleQuickComplete = async (item) => {
    try {
      await api.put(`/company-tasks/items/${item.id}`, { status: 'completed' });
      toast.success('Görev tamamlandı.');
      fetchMonthData(selectedMonth);
    } catch (error) {
      console.error('Görev tamamlanamadı', error);
      toast.error(error.response?.data?.message || 'Görev tamamlanamadı.');
    }
  };

  const handleStopRecurrence = async (item) => {
    if (!window.confirm('Bu görevin tekrarı durdurulsun mu? Bu aydan sonraki otomatik görevler oluşturulmayacak.')) return;
    try {
      await api.put(`/company-tasks/templates/${item.templateId}`, { endMonth: selectedMonth });
      toast.success('Tekrar durduruldu.');
      fetchMonthData(selectedMonth);
    } catch (error) {
      console.error('Tekrar durdurulamadı', error);
      toast.error(error.response?.data?.message || 'Tekrar durdurulamadı.');
    }
  };

  const handleDeleteThisMonth = async (item) => {
    setIsDeletingTask(true);
    try {
      await api.delete(`/company-tasks/items/${item.id}`);
      toast.success('Görev silindi.');
      setTaskToDelete(null);
      fetchMonthData(selectedMonth);
    } catch (error) {
      console.error('Görev silinemedi', error);
      toast.error(error.response?.data?.message || 'Görev silinemedi.');
    } finally {
      setIsDeletingTask(false);
    }
  };

  const handleDeleteSeries = async (item) => {
    if (!item.templateId) return;
    setIsDeletingTask(true);
    try {
      await api.delete(`/company-tasks/templates/${item.templateId}/hard`);
      toast.success('Görev serisi tamamen silindi.');
      setTaskToDelete(null);
      fetchMonthData(selectedMonth);
    } catch (error) {
      console.error('Görev serisi silinemedi', error);
      toast.error(error.response?.data?.message || 'Görev serisi silinemedi.');
    } finally {
      setIsDeletingTask(false);
    }
  };

  const handleUploadProof = async (taskId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    const loadingToast = toast.loading('Evrak yükleniyor...');
    try {
      await api.post(`/company-tasks/items/${taskId}/proof`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Evrak yüklendi.', { id: loadingToast });
      fetchMonthData(selectedMonth);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Evrak yüklenemedi.', { id: loadingToast });
    }
  };

  const handleDeleteProof = async (taskId) => {
    if (!window.confirm('Bu evrak silinsin mi? Görev durumu beklemeye dönecek.')) return;
    const loadingToast = toast.loading('Evrak siliniyor...');
    try {
      await api.delete(`/company-tasks/items/${taskId}/proof`);
      toast.success('Evrak silindi.', { id: loadingToast });
      fetchMonthData(selectedMonth);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Evrak silinemedi.', { id: loadingToast });
    }
  };

  const handleDownloadProof = (taskId) => {
    window.open(`${api.defaults.baseURL}/company-tasks/items/${taskId}/proof/download`, '_blank');
  };

  const handleViewLogs = async (task) => {
    setLogsTask(task);
    setIsLogsOpen(true);
    setIsLoadingLogs(true);
    try {
      const response = await api.get(`/company-tasks/items/${task.id}/audit-logs`);
      setTaskLogs(response.data);
    } catch (error) {
      toast.error('İşlem geçmişi yüklenemedi.');
    } finally {
      setIsLoadingLogs(false);
    }
  };

  const handleToggleMonth = async () => {
    const closing = !isMonthClosed;
    if (!window.confirm(closing ? 'Bu ay kapatılsın mı? Düzenleme kilitlenecek.' : 'Bu ay yeniden açılsın mı?')) return;
    setIsTogglingMonth(true);
    try {
      await api.post(closing ? '/company-tasks/monthly/close' : '/company-tasks/monthly/reopen', { monthKey: selectedMonth });
      toast.success(closing ? 'Ay kapatıldı.' : 'Ay açıldı.');
      fetchMonthData(selectedMonth);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Ay durumu güncellenemedi.');
    } finally {
      setIsTogglingMonth(false);
    }
  };

  const toggleCategory = (category) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(category)) next.delete(category); else next.add(category);
      return next;
    });
  };

  const recurrenceChip = (item) => {
    if (!item.templateId) return null;
    const template = templateMap[item.templateId];
    const period = template?.period || 'monthly';
    const label = template?.endMonth && period === 'monthly' ? 'Yıl sonuna kadar' : (PERIOD_LABELS[period] || 'Aylık');
    return (
      <span className="inline-flex items-center gap-1 rounded-md bg-sky-500/10 px-2 py-0.5 text-[11px] font-medium text-sky-300">
        <Repeat size={12} /> {label}
      </span>
    );
  };

  if (!canView) {
    return <Navigate to="/dashboard" />;
  }

  return (
    <Layout>
      {/* HEADER */}
      <div className="mb-6 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-xl font-bold tracking-tight text-dark-text md:text-3xl">İdari Takip</h1>
          <p className="text-sm text-dark-muted">Aylık yükümlülükler ve ödeme planı</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-1 rounded-xl border border-dark-border bg-dark-bg/50 p-1">
            <button onClick={() => setSelectedMonth((m) => shiftMonthKey(m, -1))} className="rounded-lg p-2 text-dark-muted hover:bg-dark-hover hover:text-dark-text" aria-label="Önceki ay">
              <ChevronLeft size={18} />
            </button>
            <span className="min-w-[120px] text-center text-sm font-semibold text-dark-text">{formatTaskMonth(selectedMonth)}</span>
            <button onClick={() => setSelectedMonth((m) => shiftMonthKey(m, 1))} className="rounded-lg p-2 text-dark-muted hover:bg-dark-hover hover:text-dark-text" aria-label="Sonraki ay">
              <ChevronRight size={18} />
            </button>
          </div>
          {canEdit && (
            <button onClick={handleToggleMonth} disabled={isTogglingMonth} className="flex items-center gap-1.5 rounded-xl border border-dark-border bg-dark-bg/50 px-3 py-2.5 text-sm font-medium text-dark-muted transition-colors hover:text-dark-text disabled:opacity-50" title={isMonthClosed ? 'Ayı yeniden aç' : 'Ayı kapat'}>
              {isMonthClosed ? <LockOpen size={16} /> : <Lock size={16} />}
            </button>
          )}
          {canEdit && (
            <button
              onClick={() => { setTaskToEdit(null); setIsTaskModalOpen(true); }}
              disabled={isMonthClosed}
              className="flex items-center gap-2 rounded-xl bg-primary-600 px-4 py-2.5 text-sm font-bold text-white shadow-lg shadow-primary-500/20 transition-all hover:bg-primary-500 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Plus size={18} className="stroke-[3]" /> Yeni görev
            </button>
          )}
        </div>
      </div>

      {isMonthClosed && (
        <div className="mb-4 flex items-center gap-2 rounded-xl border border-slate-600/40 bg-slate-700/20 px-4 py-2.5 text-sm text-slate-300">
          <Lock size={16} /> Bu ay kapalı — düzenleme kilitli.
        </div>
      )}

      {/* SUMMARY STRIP */}
      <div className="mb-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <div className="rounded-2xl border border-dark-border bg-dark-card p-4">
          <p className="text-xs text-dark-muted">Tamamlanma</p>
          <p className="mt-1 text-2xl font-bold text-dark-text">%{monthSummary.rate}</p>
        </div>
        <div className="rounded-2xl border border-dark-border bg-dark-card p-4">
          <p className="text-xs text-dark-muted">Açık</p>
          <p className="mt-1 text-2xl font-bold text-dark-text">{monthSummary.open}</p>
        </div>
        <div className="rounded-2xl border border-dark-border bg-dark-card p-4">
          <p className="text-xs text-dark-muted">Geciken</p>
          <p className="mt-1 text-2xl font-bold text-red-400">{monthSummary.overdue}</p>
        </div>
        <div className="rounded-2xl border border-dark-border bg-dark-card p-4">
          <p className="text-xs text-dark-muted">Belge eksik</p>
          <p className="mt-1 text-2xl font-bold text-amber-400">{monthSummary.missingProof}</p>
        </div>
      </div>

      {/* UPCOMING / OVERDUE BANNER */}
      {(monthSummary.overdue > 0 || upcomingCount > 0) && (
        <div className="mb-4 flex items-center gap-3 rounded-xl border border-amber-500/20 bg-amber-500/10 px-4 py-3">
          <Bell size={18} className="shrink-0 text-amber-300" />
          <span className="text-sm text-amber-200">
            {upcomingCount > 0 && `Önümüzdeki 7 gün içinde ${upcomingCount} görev vadesi yaklaşıyor`}
            {upcomingCount > 0 && monthSummary.overdue > 0 && ', '}
            {monthSummary.overdue > 0 && `${monthSummary.overdue} görev gecikmiş`}.
          </span>
        </div>
      )}

      {/* TOOLBAR */}
      <div className="mb-5 flex flex-col gap-3 sm:flex-row">
        <div className="relative flex-1">
          <Search size={18} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-dark-muted" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Görev ara"
            className="w-full rounded-xl border border-dark-border bg-dark-bg/50 py-2.5 pl-10 pr-4 text-dark-text placeholder:text-dark-muted focus:border-primary-500 focus:outline-none"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="rounded-xl border border-dark-border bg-dark-bg/50 px-4 py-2.5 text-dark-text focus:border-primary-500 focus:outline-none sm:w-56"
        >
          {STATUS_FILTER_OPTIONS.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
        </select>
        <select
          value={ownerFilter}
          onChange={(e) => setOwnerFilter(e.target.value)}
          className="rounded-xl border border-dark-border bg-dark-bg/50 px-4 py-2.5 text-dark-text focus:border-primary-500 focus:outline-none sm:w-48"
        >
          {canEdit && <option value="all">Tüm sorumlular</option>}
          <option value="mine">Benim görevlerim</option>
        </select>
        <select
          value={groupMode}
          onChange={(e) => setGroupMode(e.target.value)}
          className="rounded-xl border border-dark-border bg-dark-bg/50 px-4 py-2.5 text-dark-text focus:border-primary-500 focus:outline-none sm:w-48"
        >
          <option value="priority">Önceliğe göre</option>
          <option value="category">Kategoriye göre</option>
        </select>
      </div>

      {/* CATEGORY GROUPS */}
      {isLoading ? (
        <div className="rounded-2xl border border-dark-border bg-dark-card p-10 text-center text-dark-muted">Veriler yükleniyor...</div>
      ) : groupedItems.length === 0 ? (
        <div className="rounded-2xl border border-dark-border bg-dark-card p-10 text-center text-dark-muted">Bu filtreye uyan görev bulunamadı.</div>
      ) : (
        <div className="flex flex-col gap-3">
          {groupedItems.map(([category, items]) => {
            const isCollapsed = collapsed.has(category);
            const groupTotal = items.reduce((acc, i) => {
              acc[i.currency || 'TRY'] = (acc[i.currency || 'TRY'] || 0) + Number(i.plannedAmount || 0);
              return acc;
            }, {});
            const totalLabel = Object.entries(groupTotal).map(([cur, amt]) => formatTaskAmount(amt, cur)).join(' · ');
            return (
              <div key={category} className="overflow-hidden rounded-2xl border border-dark-border bg-dark-card">
                <button onClick={() => toggleCategory(category)} className="flex w-full items-center justify-between bg-dark-bg/40 px-4 py-3 text-left transition-colors hover:bg-dark-hover">
                  <span className="flex items-center gap-2 text-sm font-semibold text-dark-text">
                    <ChevronDown size={16} className={`transition-transform ${isCollapsed ? '-rotate-90' : ''}`} />
                    {category}
                    <span className="text-xs font-normal text-dark-muted">{items.length} görev</span>
                  </span>
                  <span className="text-xs text-dark-muted">{totalLabel}</span>
                </button>

                {!isCollapsed && items.map((item) => {
                  const statusMeta = getCompanyTaskStatusMeta(item.displayStatus);
                  const canWrite = hasWritePermission(item);
                  return (
                    <div key={item.id} className="flex flex-col gap-3 border-t border-dark-border px-4 py-3 sm:flex-row sm:items-center">
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="text-sm font-semibold text-dark-text">{item.title}</span>
                          {recurrenceChip(item)}
                        </div>
                        <p className="mt-1 text-xs text-dark-muted">
                          Son gün {formatTaskDate(item.dueDate)} · {formatTaskAmount(item.plannedAmount, item.currency)}
                          {formatAssignedUsers(item) ? ` · ${formatAssignedUsers(item)}` : ''}
                        </p>
                      </div>

                      <div className="flex flex-wrap items-center gap-2">
                        {item.requiresProof && (
                          item.proofReceived ? (
                            <span className="inline-flex items-center gap-1 rounded-md bg-emerald-500/10 px-2 py-1 text-[11px] font-medium text-emerald-300">Belge var</span>
                          ) : (
                            <span className="inline-flex items-center gap-1 rounded-md bg-amber-500/10 px-2 py-1 text-[11px] font-medium text-amber-300">Belge eksik</span>
                          )
                        )}
                        <span className={`inline-flex rounded-md border px-2 py-1 text-[11px] font-semibold ${statusMeta.className}`}>{statusMeta.label}</span>

                        {item.requiresProof && item.proofReceived && (
                          <button onClick={() => handleDownloadProof(item.id)} className="rounded-lg p-2 text-sky-400 hover:bg-sky-500/10" title="Belgeyi indir"><Download size={16} /></button>
                        )}
                        {item.requiresProof && item.proofReceived && canWrite && (
                          <button onClick={() => handleDeleteProof(item.id)} className="rounded-lg p-2 text-dark-muted hover:bg-red-500/10 hover:text-red-400" title="Belgeyi sil"><X size={16} /></button>
                        )}
                        {item.requiresProof && !item.proofReceived && canWrite && (
                          <label className="cursor-pointer rounded-lg p-2 text-amber-400 hover:bg-amber-500/10" title="Belge yükle">
                            <Upload size={16} />
                            <input type="file" className="sr-only" accept="image/*,application/pdf" onChange={(e) => { if (e.target.files?.[0]) handleUploadProof(item.id, e.target.files[0]); }} />
                          </label>
                        )}

                        {canWrite && !item.isCompleted && (!item.requiresProof || item.proofReceived) && (
                          <button onClick={() => handleQuickComplete(item)} className="flex items-center gap-1.5 rounded-lg border border-emerald-600/40 bg-emerald-500/10 px-2.5 py-1.5 text-xs font-semibold text-emerald-300 transition-all hover:bg-emerald-500/20" title="Tamamlandı işaretle">
                            <CheckCircle2 size={14} /> Tamamla
                          </button>
                        )}

                        <button onClick={() => handleViewLogs(item)} className="rounded-lg p-2 text-dark-muted hover:bg-dark-hover hover:text-dark-text" title="İşlem geçmişi"><History size={16} /></button>

                        {canEdit && !isMonthClosed && (
                          <button onClick={() => { setTaskToEdit(item); setIsTaskModalOpen(true); }} className="rounded-lg p-2 text-dark-muted hover:bg-blue-500/10 hover:text-blue-400" title="Düzenle"><Pencil size={16} /></button>
                        )}
                        {canEdit && !isMonthClosed && item.templateId && (
                          <button onClick={() => handleStopRecurrence(item)} className="rounded-lg p-2 text-dark-muted hover:bg-red-500/10 hover:text-red-400" title="Tekrarı durdur"><CalendarOff size={16} /></button>
                        )}
                        {canEdit && !isMonthClosed && (
                          <button onClick={() => setTaskToDelete(item)} className="rounded-lg p-2 text-dark-muted hover:bg-red-500/10 hover:text-red-400" title="Sil"><Trash2 size={16} /></button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      )}

      <CompanyTaskModal
        isOpen={isTaskModalOpen}
        onClose={() => { setIsTaskModalOpen(false); setTaskToEdit(null); }}
        onSubmit={handleSubmitTask}
        initialData={taskToEdit}
        monthKey={selectedMonth}
        isSubmitting={isSavingTask}
        categoryOptions={categoryOptions}
        users={users}
        template={taskToEdit?.templateId ? templateMap[taskToEdit.templateId] : null}
      />

      {/* GÖREV SİLME */}
      {taskToDelete && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-slate-950/80 p-4 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-2xl border border-slate-800 bg-slate-900 text-white shadow-2xl">
            <div className="flex items-start justify-between gap-3 border-b border-slate-800/80 p-5">
              <div className="min-w-0">
                <h3 className="text-lg font-bold text-white">Görevi sil</h3>
                <p className="mt-1 truncate text-xs font-medium text-slate-400">{taskToDelete.title}</p>
              </div>
              <button onClick={() => setTaskToDelete(null)} className="rounded-xl p-2 text-slate-400 transition-colors hover:bg-slate-800 hover:text-white"><X size={18} /></button>
            </div>
            <div className="space-y-4 p-6">
              {taskToDelete.templateId ? (
                <>
                  <p className="text-sm text-slate-300">Bu tekrarlayan bir görev. Ne silinsin?</p>
                  <div className="space-y-2">
                    <button
                      type="button"
                      disabled={isDeletingTask}
                      onClick={() => handleDeleteThisMonth(taskToDelete)}
                      className="w-full rounded-xl border border-slate-700 px-4 py-3 text-left text-sm font-medium text-slate-100 transition-all hover:border-slate-600 hover:bg-slate-800 disabled:opacity-50"
                    >
                      Sadece bu ay
                      <span className="mt-0.5 block text-xs font-normal text-slate-400">Seri sonraki aylarda devam eder.</span>
                    </button>
                    <button
                      type="button"
                      disabled={isDeletingTask}
                      onClick={() => handleDeleteSeries(taskToDelete)}
                      className="w-full rounded-xl border border-red-600/40 bg-red-500/10 px-4 py-3 text-left text-sm font-semibold text-red-300 transition-all hover:bg-red-500/20 disabled:opacity-50"
                    >
                      Tüm seri (tüm aylar)
                      <span className="mt-0.5 block text-xs font-normal text-red-300/80">Şablon ve tüm ayların kayıtları (dekontlar dahil) kalıcı silinir.</span>
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <p className="text-sm text-slate-300">Bu görev kalıcı olarak silinsin mi?</p>
                  <button
                    type="button"
                    disabled={isDeletingTask}
                    onClick={() => handleDeleteThisMonth(taskToDelete)}
                    className="w-full rounded-xl border border-red-600/40 bg-red-500/10 px-4 py-3 text-sm font-semibold text-red-300 transition-all hover:bg-red-500/20 disabled:opacity-50"
                  >
                    Sil
                  </button>
                </>
              )}
              <button
                type="button"
                onClick={() => setTaskToDelete(null)}
                className="w-full rounded-xl px-4 py-2.5 text-sm font-medium text-slate-300 transition-colors hover:bg-slate-800"
              >
                Vazgeç
              </button>
            </div>
          </div>
        </div>
      )}

      {/* AUDIT LOGS */}
      {isLogsOpen && logsTask && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-slate-950/80 p-4 backdrop-blur-sm">
          <div className="flex max-h-[80vh] w-full max-w-lg flex-col rounded-2xl border border-slate-800 bg-slate-900 text-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-slate-800/80 p-5">
              <div>
                <h3 className="text-lg font-bold text-white">İşlem geçmişi</h3>
                <p className="mt-1 truncate text-xs font-medium text-slate-400">{logsTask.title}</p>
              </div>
              <button onClick={() => { setIsLogsOpen(false); setLogsTask(null); setTaskLogs([]); }} className="rounded-xl p-2 text-slate-400 transition-colors hover:bg-slate-800 hover:text-white"><X size={18} /></button>
            </div>
            <div className="flex-1 space-y-4 overflow-y-auto p-6">
              {isLoadingLogs ? (
                <div className="py-12 text-center text-sm text-slate-400">Yükleniyor...</div>
              ) : taskLogs.length === 0 ? (
                <div className="py-12 text-center text-sm text-slate-400">İşlem kaydı bulunamadı.</div>
              ) : (
                <div className="ml-2 space-y-6 border-l border-slate-800 pl-4">
                  {taskLogs.map((log) => (
                    <div key={log.id} className="relative">
                      <div className="absolute -left-[21px] top-1.5 h-2.5 w-2.5 rounded-full border-2 border-slate-900 bg-slate-700" />
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">{log.actionType}</span>
                        <span className="text-[10px] font-medium text-slate-500">{formatAuditDateTime(log.createdAt)}</span>
                      </div>
                      <p className="mt-2 text-sm font-medium text-slate-200">{log.note || 'Görev güncellendi.'}</p>
                      {log.oldValue && log.newValue && log.actionType === 'status_change' && (
                        <p className="mt-1 text-xs text-slate-400">
                          <code className="rounded bg-slate-950 px-1 text-red-300">{log.oldValue}</code> → <code className="rounded bg-slate-950 px-1 text-emerald-300">{log.newValue}</code>
                        </p>
                      )}
                      <p className="mt-1.5 text-xs font-semibold text-slate-500">Yapan: {log.userId}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default CompanyTasks;
